import React, {useState} from "react";
import {Button} from "primereact/button";
import {InputSwitch} from "primereact/inputswitch";
import {Tag} from "primereact/tag";
import Icon from "../../system/components/icon";
import {notificationApi} from "../../models/notification/notification-api";
import {disablePush, enablePush} from "../../system/push/push-client";
import PushDiagnostics from "./push-diagnostics";

const PUSH_ERRORS = {
    "push.blocked": "اجازه اعلان در این مرورگر مسدود شده است. از تنظیمات سایت در مرورگر، اعلان‌ها را مجاز کنید.",
    "push.dismissed": "اجازه اعلان داده نشد. دوباره روی «فعال‌سازی» بزنید و «Allow» را انتخاب کنید.",
    "push.unsupported": "این مرورگر Push را پشتیبانی نمی‌کند.",
    "push.install-required": "در آیفون، ابتدا زوفینو را با «Add to Home Screen» نصب کنید و از همان‌جا باز کنید.",
    "push.not-configured": "Push روی سرور هنوز پیکربندی نشده است.",
    "push.worker-failed": "سرویس اعلان مرورگر راه‌اندازی نشد. صفحه را دوباره بارگذاری کنید.",
    "push.service-unreachable": "مرورگر نتوانست به سرویس Push خودش وصل شود (معمولاً محدودیت شبکه). «عیب‌یابی» را باز کنید؛ تا وقتی تب زوفینو باز است اعلان‌ها روی همین دستگاه نمایش داده می‌شوند.",
};

function ChannelRow({icon, title, subtitle, status, children, disabled}) {
    return (
        <div className={`flex flex-wrap align-items-center justify-content-between gap-3 p-3 border-round-xl border-1 border-200 ${disabled ? "opacity-60" : ""}`}>
            <div className="flex align-items-center gap-3 min-w-0">
                <div className="w-3rem h-3rem border-circle surface-100 flex flex-shrink-0 align-items-center justify-content-center text-primary">
                    <Icon icon={`mi mi:${icon}`} className="text-xl"/>
                </div>
                <div className="flex flex-column gap-1 min-w-0">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <strong className="text-sm">{title}</strong>
                        {status}
                    </div>
                    {subtitle && <small className="text-xs text-500 line-height-3">{subtitle}</small>}
                </div>
            </div>
            <div className="flex align-items-center gap-2 flex-wrap">{children}</div>
        </div>
    );
}

export default function ChannelsSection({channels, preference, onPreferenceChange, browserState, onRefresh, toast}) {
    const [busy, setBusy] = useState(null);
    const [diagnostics, setDiagnostics] = useState(false);
    const [diagnosticsKey, setDiagnosticsKey] = useState(0);
    const push = channels?.browser_push || {};
    const devices = push.devices || [];

    const run = async (key, action) => {
        if (busy) return;
        setBusy(key);
        try {
            await action();
        } finally {
            setBusy(null);
        }
    };

    const activate = () => run("enable", async () => {
        try {
            await enablePush(push.publicKey);
            toast("success", "Push مرورگر فعال شد.");
        } catch (error) {
            toast("error", PUSH_ERRORS[error?.message] || "فعال‌سازی Push انجام نشد. دوباره تلاش کنید.");
        }
        await onRefresh();
    });

    const deactivate = () => run("disable", async () => {
        try {
            await disablePush();
            toast("info", "Push برای این مرورگر غیرفعال شد.");
        } catch (error) {
            toast("error", "غیرفعال‌سازی انجام نشد. دوباره تلاش کنید.");
        }
        await onRefresh();
    });

    const test = () => run("test", async () => {
        try {
            const result = await notificationApi.testPush();
            const total = (result?.results || []).length;
            if (result?.sent) toast("success", `اعلان آزمایشی به ${result.sent.toLocaleString("fa-IR")} از ${total.toLocaleString("fa-IR")} دستگاه ارسال شد. اگر نمایش داده نشد، «عیب‌یابی» را باز کنید.`);
            else toast("warn", "سرویس Push پیام را نپذیرفت. «ثبت دوباره این مرورگر» را در عیب‌یابی بزنید.");
            setDiagnosticsKey((value) => value + 1);
        } catch (error) {
            toast("error", "ارسال آزمایشی انجام نشد.");
        }
    });

    const pushStatus = {
        enabled: <Tag value="فعال" severity="success" rounded/>,
        permission_required: <Tag value="نیاز به اجازه مرورگر" severity="secondary" rounded/>,
        blocked: <Tag value="مسدود در مرورگر" severity="danger" rounded/>,
        unsupported: <Tag value="پشتیبانی نمی‌شود" severity="secondary" rounded/>,
        install_required: <Tag value="نیاز به نصب" severity="warning" rounded/>,
        loading: <Tag value="در حال بررسی…" severity="secondary" rounded/>,
    }[browserState] || null;

    const pushSubtitle = {
        enabled: devices.length > 1
            ? `روی این مرورگر فعال است · ${devices.length.toLocaleString("fa-IR")} دستگاه متصل`
            : "Push مرورگر روی این دستگاه فعال است.",
        permission_required: devices.length
            ? `روی ${devices.length.toLocaleString("fa-IR")} دستگاه دیگر فعال است؛ برای این مرورگر هم فعال کنید.`
            : "حتی وقتی زوفینو بسته است، اعلان‌های مهم روی دسکتاپ یا موبایل نمایش داده می‌شوند.",
        blocked: PUSH_ERRORS["push.blocked"],
        unsupported: PUSH_ERRORS["push.unsupported"],
        install_required: PUSH_ERRORS["push.install-required"],
    }[browserState];

    return (
        <section className="ns-card">
            <h2 className="ns-card-title"><span className="ns-icon"><i className="pi pi-send"/></span>کانال‌های دریافت</h2>
            <p className="ns-card-description">اعلان داخل زوفینو همیشه فعال است. کانال‌های دیگر را اینجا روشن کنید؛ در حالت «سفارشی» برای هر رویداد جداگانه تعیین می‌کنید.</p>
            <div className="flex flex-column gap-2">
                <ChannelRow icon="inbox" title="داخل زوفینو" subtitle="بخش «اعلان‌ها» و زنگوله بالای صفحه" status={<Tag value="همیشه فعال" severity="success" rounded/>}/>

                <ChannelRow icon="web" title="Push مرورگر" subtitle={pushSubtitle} status={pushStatus} disabled={!push.available}>
                    {browserState === "enabled" ? (
                        <>
                            <Button label="ارسال تست" icon="pi pi-send" size="small" text className="text-xs gap-1" loading={busy === "test"} disabled={Boolean(busy)} onClick={test}/>
                            <Button label="غیرفعال در این مرورگر" size="small" text severity="secondary" className="text-xs" loading={busy === "disable"} disabled={Boolean(busy)} onClick={deactivate}/>
                        </>
                    ) : (browserState === "permission_required" && push.available) ? (
                        <Button label="فعال‌سازی" icon="pi pi-bell" size="small" className="text-xs gap-1" loading={busy === "enable"} disabled={Boolean(busy)} onClick={activate}/>
                    ) : null}
                    {push.available && (
                        <span className="flex align-items-center gap-2 text-xs text-600 mr-2" title="دریافت Push روی همه دستگاه‌ها">
                            <InputSwitch
                                checked={preference.channels?.browser_push !== false}
                                onChange={(event) => onPreferenceChange({channels: {...preference.channels, browser_push: event.value}})}
                            />
                            همه دستگاه‌ها
                        </span>
                    )}
                </ChannelRow>

                {push.available && (
                    <div className="border-round-xl border-1 border-200 overflow-hidden">
                        <button type="button" className="ns-plain-button w-full flex align-items-center justify-content-between gap-2 px-3 py-2 text-xs text-700" onClick={() => setDiagnostics(!diagnostics)} aria-expanded={diagnostics}>
                            <span className="flex align-items-center gap-2"><i className="pi pi-wrench text-500"/>عیب‌یابی Push روی این دستگاه</span>
                            <i className={`pi pi-chevron-down text-500 ${diagnostics ? "rotate-180" : ""}`}/>
                        </button>
                        {diagnostics && (
                            <div className="px-3 pb-3 border-top-1 border-100">
                                <PushDiagnostics key={diagnosticsKey} publicKey={push.publicKey} onChanged={onRefresh} toast={toast}/>
                            </div>
                        )}
                    </div>
                )}

                <ChannelRow icon="mail" title="ایمیل" subtitle="برای گزارش‌ها، امنیت و موارد مهم" status={<Tag value="به‌زودی" severity="secondary" rounded/>} disabled/>
                <ChannelRow icon="smartphone" title="اپ موبایل" subtitle="بعد از ورود در اپلیکیشن موبایل، این کانال خودکار فعال می‌شود." status={<Tag value="دستگاهی متصل نیست" severity="secondary" rounded/>} disabled/>
            </div>
        </section>
    );
}
